import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';

const ProgressDashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/parent/dashboard`, {
        withCredentials: true
      });
      setDashboardData(response.data);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
      setError('Failed to load progress data');
    } finally {
      setLoading(false);
    }
  };

  const getProgressPercentage = (completed, total) => {
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-400 mx-auto"></div>
          <p className="mt-4 text-white text-lg">Loading your progress...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center">
        <div className="text-center bg-white/10 backdrop-blur-lg rounded-2xl p-8">
          <p className="text-red-400 text-lg mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-yellow-500 text-gray-900 rounded-lg font-semibold hover:bg-yellow-400"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const aiProgress = dashboardData?.own_progress?.ai_course || { completed: 0, total_started: 0, lessons: [] };
  const codingProgress = dashboardData?.own_progress?.coding_course || { completed: 0, total_started: 0, lessons: [] };
  const subscriptions = dashboardData?.subscriptions || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 p-4 md:p-8">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              📊 My Learning Progress
            </h1>
            <p className="text-purple-200">
              Track your journey through Kids Genie Academy
            </p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-white/10 backdrop-blur-lg border-2 border-white/20 rounded-xl text-white font-semibold hover:bg-white/20 transition-all"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>

      {/* User Info Card */}
      <div className="max-w-6xl mx-auto mb-8">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-3xl">
              👤
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">
                {dashboardData?.parent?.name || user?.name || 'Student'}
              </h2>
              <p className="text-purple-200">{dashboardData?.parent?.email || user?.email}</p>
              <div className="flex gap-2 mt-2">
                {subscriptions.includes('AI') && (
                  <span className="px-3 py-1 bg-cyan-500/30 text-cyan-300 rounded-full text-sm font-medium">
                    🤖 AI Course
                  </span>
                )}
                {subscriptions.includes('Coding') && (
                  <span className="px-3 py-1 bg-green-500/30 text-green-300 rounded-full text-sm font-medium">
                    💻 Coding Course
                  </span>
                )}
                {subscriptions.length === 0 && (
                  <span className="px-3 py-1 bg-gray-500/30 text-gray-300 rounded-full text-sm font-medium">
                    No active subscriptions
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Overview */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* AI Course Progress */}
        <div className="bg-gradient-to-br from-cyan-600/30 to-blue-600/30 backdrop-blur-lg rounded-2xl p-6 border border-cyan-400/30">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">🤖</span>
            <div>
              <h3 className="text-xl font-bold text-white">AI Course</h3>
              <p className="text-cyan-200 text-sm">Artificial Intelligence Basics</p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm text-white mb-2">
              <span>Progress</span>
              <span>{aiProgress.completed} / 10 Lessons</span>
            </div>
            <div className="h-4 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full transition-all duration-500"
                style={{ width: `${getProgressPercentage(aiProgress.completed, 10)}%` }}
              />
            </div>
            <p className="text-cyan-300 text-sm mt-2">
              {getProgressPercentage(aiProgress.completed, 10)}% Complete
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-white">{aiProgress.completed}</p>
              <p className="text-cyan-200 text-sm">Completed</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-white">{10 - aiProgress.completed}</p>
              <p className="text-cyan-200 text-sm">Remaining</p>
            </div>
          </div>

          {!subscriptions.includes('AI') && (
            <button
              onClick={() => navigate('/checkout')}
              className="w-full mt-4 py-3 bg-cyan-500 text-white rounded-xl font-semibold hover:bg-cyan-400 transition-all"
            >
              Subscribe to AI Course
            </button>
          )}
        </div>

        {/* Coding Course Progress */}
        <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/30 backdrop-blur-lg rounded-2xl p-6 border border-green-400/30">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">💻</span>
            <div>
              <h3 className="text-xl font-bold text-white">Coding Course</h3>
              <p className="text-green-200 text-sm">Programming Fundamentals</p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm text-white mb-2">
              <span>Progress</span>
              <span>{codingProgress.completed} / 10 Lessons</span>
            </div>
            <div className="h-4 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-500"
                style={{ width: `${getProgressPercentage(codingProgress.completed, 10)}%` }}
              />
            </div>
            <p className="text-green-300 text-sm mt-2">
              {getProgressPercentage(codingProgress.completed, 10)}% Complete
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-white">{codingProgress.completed}</p>
              <p className="text-green-200 text-sm">Completed</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-white">{10 - codingProgress.completed}</p>
              <p className="text-green-200 text-sm">Remaining</p>
            </div>
          </div>

          {!subscriptions.includes('Coding') && (
            <button
              onClick={() => navigate('/checkout')}
              className="w-full mt-4 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-400 transition-all"
            >
              Subscribe to Coding Course
            </button>
          )}
        </div>
      </div>

      {/* Detailed Lesson Progress */}
      <div className="max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold text-white mb-6">📚 Lesson Details</h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* AI Lessons */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span>🤖</span> AI Course Lessons
            </h3>
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => {
                const lessonNum = i + 1;
                const lessonProgress = aiProgress.lessons?.find(l => 
                  l.lesson_id?.includes(`week-${lessonNum}`) || 
                  l.lesson_id?.includes(`lesson-${lessonNum}`)
                );
                const isCompleted = lessonProgress?.completed;
                
                return (
                  <div 
                    key={i}
                    className={`flex items-center justify-between p-3 rounded-xl transition-all ${
                      isCompleted 
                        ? 'bg-cyan-500/30 border border-cyan-400/50' 
                        : 'bg-white/5 border border-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        isCompleted ? 'bg-cyan-500 text-white' : 'bg-white/20 text-white/60'
                      }`}>
                        {isCompleted ? '✓' : lessonNum}
                      </span>
                      <span className={`font-medium ${isCompleted ? 'text-white' : 'text-white/60'}`}>
                        AI Lesson {lessonNum}
                      </span>
                    </div>
                    {isCompleted && (
                      <span className="text-xs text-cyan-300">
                        {formatDate(lessonProgress?.completed_at)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Coding Lessons */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span>💻</span> Coding Course Lessons
            </h3>
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => {
                const lessonNum = i + 1;
                const lessonProgress = codingProgress.lessons?.find(l => 
                  l.lesson_id?.includes(`coding`) && l.lesson_id?.includes(`${lessonNum}`)
                );
                const isCompleted = lessonProgress?.completed;
                
                return (
                  <div 
                    key={i}
                    className={`flex items-center justify-between p-3 rounded-xl transition-all ${
                      isCompleted 
                        ? 'bg-green-500/30 border border-green-400/50' 
                        : 'bg-white/5 border border-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        isCompleted ? 'bg-green-500 text-white' : 'bg-white/20 text-white/60'
                      }`}>
                        {isCompleted ? '✓' : lessonNum}
                      </span>
                      <span className={`font-medium ${isCompleted ? 'text-white' : 'text-white/60'}`}>
                        Coding Lesson {lessonNum}
                      </span>
                    </div>
                    {isCompleted && (
                      <span className="text-xs text-green-300">
                        {formatDate(lessonProgress?.completed_at)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Details */}
      {dashboardData?.subscription_details && (
        <div className="max-w-6xl mx-auto mt-8">
          <h2 className="text-2xl font-bold text-white mb-6">📅 Subscription Details</h2>
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {dashboardData.subscription_details.ai_course_start && (
                <div className="bg-cyan-500/20 rounded-xl p-4 border border-cyan-400/30">
                  <h4 className="text-lg font-bold text-white mb-2">🤖 AI Course</h4>
                  <p className="text-cyan-200 text-sm">
                    Started: {formatDate(dashboardData.subscription_details.ai_course_start)}
                  </p>
                  <p className="text-cyan-200 text-sm">
                    Expires: {formatDate(dashboardData.subscription_details.ai_course_expires)}
                  </p>
                  <p className="text-yellow-300 text-sm mt-2">
                    ⏰ 6 months access from enrollment
                  </p>
                </div>
              )}
              {dashboardData.subscription_details.coding_course_start && (
                <div className="bg-green-500/20 rounded-xl p-4 border border-green-400/30">
                  <h4 className="text-lg font-bold text-white mb-2">💻 Coding Course</h4>
                  <p className="text-green-200 text-sm">
                    Started: {formatDate(dashboardData.subscription_details.coding_course_start)}
                  </p>
                  <p className="text-green-200 text-sm">
                    Expires: {formatDate(dashboardData.subscription_details.coding_course_expires)}
                  </p>
                  <p className="text-yellow-300 text-sm mt-2">
                    ⏰ 6 months access from enrollment
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Achievement Section */}
      <div className="max-w-6xl mx-auto mt-8">
        <h2 className="text-2xl font-bold text-white mb-6">🏆 Achievements</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* First Lesson Badge */}
          <div className={`rounded-2xl p-4 text-center transition-all ${
            (aiProgress.completed > 0 || codingProgress.completed > 0)
              ? 'bg-yellow-500/30 border-2 border-yellow-400'
              : 'bg-white/5 border-2 border-white/10 opacity-50'
          }`}>
            <span className="text-4xl">🌟</span>
            <p className="text-white font-semibold mt-2">First Steps</p>
            <p className="text-white/60 text-xs">Complete your first lesson</p>
          </div>

          {/* Half AI Course */}
          <div className={`rounded-2xl p-4 text-center transition-all ${
            aiProgress.completed >= 5
              ? 'bg-cyan-500/30 border-2 border-cyan-400'
              : 'bg-white/5 border-2 border-white/10 opacity-50'
          }`}>
            <span className="text-4xl">🤖</span>
            <p className="text-white font-semibold mt-2">AI Explorer</p>
            <p className="text-white/60 text-xs">Complete 5 AI lessons</p>
          </div>

          {/* Half Coding Course */}
          <div className={`rounded-2xl p-4 text-center transition-all ${
            codingProgress.completed >= 5
              ? 'bg-green-500/30 border-2 border-green-400'
              : 'bg-white/5 border-2 border-white/10 opacity-50'
          }`}>
            <span className="text-4xl">💻</span>
            <p className="text-white font-semibold mt-2">Code Apprentice</p>
            <p className="text-white/60 text-xs">Complete 5 Coding lessons</p>
          </div>

          {/* Complete Both Courses */}
          <div className={`rounded-2xl p-4 text-center transition-all ${
            (aiProgress.completed >= 10 && codingProgress.completed >= 10)
              ? 'bg-gradient-to-br from-yellow-500/50 to-orange-500/50 border-2 border-yellow-400'
              : 'bg-white/5 border-2 border-white/10 opacity-50'
          }`}>
            <span className="text-4xl">🎓</span>
            <p className="text-white font-semibold mt-2">Master Wizard</p>
            <p className="text-white/60 text-xs">Complete both courses</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="max-w-6xl mx-auto mt-12 text-center">
        <p className="text-purple-200 text-sm">
          Keep learning and unlock more achievements! 🚀
        </p>
      </div>
    </div>
  );
};

export default ProgressDashboard;
